import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { BizyMomentEmailPreferencesSchema } from '../schemas/bizy-moment-email-preferences';
import { headers } from './meta';

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	MomentEmailPreferences = 'bizy-moment-email-preferences',
}

export const bizyMomentEmailPreferencesQueryKey = () => [QueryKey.MomentEmailPreferences];

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyMomentEmailPreferencesPath = (prefix = '') => `${prefix}/bizy/email_preferences`;

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches the current user's Bizy moment-email consent (default-on).
 *
 * GET /api/v2/bizy/email_preferences
 */
export const useBizyMomentEmailPreferences = (enabled = true) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyMomentEmailPreferencesQueryKey(),
		queryFn: async () => {
			const res = await http.get(bizyMomentEmailPreferencesPath(), { headers });
			return BizyMomentEmailPreferencesSchema.parse(res.data);
		},
		enabled,
	});
};

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/**
 * Persists Bizy moment-email consent for the current user. Request body is
 * snake_case (only responses are camelized); the response mirrors GET, so we
 * write it straight back into the cache.
 *
 * PATCH /api/v2/bizy/email_preferences
 */
export const useUpdateBizyMomentEmailPreferencesMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ momentEmailsEnabled }: { momentEmailsEnabled: boolean }) => {
			const res = await http.patch(bizyMomentEmailPreferencesPath(), { moment_emails_enabled: momentEmailsEnabled }, { headers });
			return BizyMomentEmailPreferencesSchema.parse(res.data);
		},
		onSuccess: (data) => {
			queryClient.setQueryData(bizyMomentEmailPreferencesQueryKey(), data);
		},
	});
};
